import * as React from 'react';
import {
  CHART_INK,
  CHART_MARGIN,
  defaultXLabel,
  formatCompact,
  labelStride,
  niceTicks,
  resolveSeriesColors,
  seriesValue,
  useMeasure,
  type ChartSeries,
  type TimePoint,
} from './chart-utils';
import { ChartLegend } from './chart-legend';
import { ChartTooltip, type ChartTooltipState } from './chart-tooltip';

export type TimeSeriesBarChartProps = {
  data: TimePoint[];
  series: ChartSeries[];
  stacked?: boolean;
  height?: number;
  valueFormat?: (n: number) => string;
  xLabelFormat?: (bucket: string) => string;
  emptyLabel?: string;
  className?: string;
};

const MAX_BAR = 24;
const GAP = 2;

/**
 * Vertical bars per time bucket. One series → single bars; several series →
 * stacked (with 2px surface gaps) or grouped.
 */
export function TimeSeriesBarChart({
  data,
  series,
  stacked = false,
  height = 240,
  valueFormat = formatCompact,
  xLabelFormat = defaultXLabel,
  emptyLabel = 'No data',
  className,
}: TimeSeriesBarChartProps) {
  const [ref, { width }] = useMeasure<HTMLDivElement>();
  const [hover, setHover] = React.useState<number | null>(null);
  const colors = resolveSeriesColors(series);

  const plotW = Math.max(0, width - CHART_MARGIN.left - CHART_MARGIN.right);
  const plotH = Math.max(0, height - CHART_MARGIN.top - CHART_MARGIN.bottom);

  const isStacked = stacked && series.length > 1;
  const maxValue = React.useMemo(() => {
    let max = 0;
    for (const point of data) {
      if (isStacked) {
        const total = series.reduce((sum, s) => sum + seriesValue(point, s.key), 0);
        max = Math.max(max, total);
      } else {
        for (const s of series) max = Math.max(max, seriesValue(point, s.key));
      }
    }
    return max;
  }, [data, series, isStacked]);

  const ticks = niceTicks(maxValue);
  const yMax = ticks[ticks.length - 1] || 1;
  const yScale = (v: number) => plotH - (v / yMax) * plotH;

  const slot = data.length > 0 ? plotW / data.length : 0;
  const groupCount = isStacked ? 1 : series.length || 1;
  const barW = Math.min(MAX_BAR, Math.max(2, (slot - GAP * (groupCount + 1)) / groupCount));
  const groupW = barW * groupCount + GAP * (groupCount - 1);
  const stride = labelStride(data.length, plotW);

  const tooltip: ChartTooltipState | null =
    hover !== null && data[hover]
      ? {
          x: CHART_MARGIN.left + hover * slot + slot / 2,
          y: CHART_MARGIN.top,
          title: xLabelFormat(data[hover].bucket),
          rows: series.map((s, i) => ({
            label: s.label,
            value: valueFormat(seriesValue(data[hover], s.key)),
            color: colors[i],
          })),
        }
      : null;

  return (
    <div className={['w-full', className].filter(Boolean).join(' ')}>
      <ChartLegend series={series} className="mb-2" />
      <div ref={ref} className="relative w-full" style={{ height }} data-testid="bar-chart">
        {data.length === 0 ? (
          <div
            className="flex h-full items-center justify-center text-sm"
            style={{ color: CHART_INK.muted }}
          >
            {emptyLabel}
          </div>
        ) : (
          <svg width={width} height={height} role="img" onMouseLeave={() => setHover(null)}>
            <g transform={`translate(${CHART_MARGIN.left},${CHART_MARGIN.top})`}>
              {ticks.map((t) => (
                <g key={t} transform={`translate(0,${yScale(t)})`}>
                  <line x1={0} x2={plotW} stroke={CHART_INK.grid} strokeWidth={1} />
                  <text
                    x={-8}
                    dy="0.32em"
                    textAnchor="end"
                    fontSize={11}
                    fill={CHART_INK.muted}
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    {valueFormat(t)}
                  </text>
                </g>
              ))}
              {data.map((point, idx) => {
                const x0 = idx * slot;
                const hovered = hover === idx;
                let stackY = plotH;
                return (
                  <g key={point.bucket} data-testid="bar-group">
                    {hovered ? (
                      <rect x={x0} y={0} width={slot} height={plotH} fill={CHART_INK.track} opacity={0.6} />
                    ) : null}
                    {series.map((s, si) => {
                      const v = seriesValue(point, s.key);
                      if (isStacked) {
                        const h = (v / yMax) * plotH;
                        const top = stackY - h;
                        const isLast = si === series.length - 1;
                        const gapH = si === 0 ? 0 : GAP;
                        stackY = top;
                        if (h <= 0) return null;
                        const bx = x0 + (slot - barW) / 2;
                        return (
                          <BarRect
                            key={s.key}
                            x={bx}
                            y={top + gapH}
                            width={barW}
                            height={Math.max(0, h - gapH)}
                            color={colors[si]}
                            rounded={isLast}
                          />
                        );
                      }
                      const h = (v / yMax) * plotH;
                      if (h <= 0) return null;
                      const bx = x0 + (slot - groupW) / 2 + si * (barW + GAP);
                      return (
                        <BarRect
                          key={s.key}
                          x={bx}
                          y={plotH - h}
                          width={barW}
                          height={h}
                          color={colors[si]}
                          rounded
                        />
                      );
                    })}
                    <rect
                      x={x0}
                      y={0}
                      width={slot}
                      height={plotH}
                      fill="transparent"
                      onMouseEnter={() => setHover(idx)}
                    />
                    {idx % stride === 0 ? (
                      <text
                        x={x0 + slot / 2}
                        y={plotH + 18}
                        textAnchor="middle"
                        fontSize={11}
                        fill={CHART_INK.muted}
                      >
                        {xLabelFormat(point.bucket)}
                      </text>
                    ) : null}
                  </g>
                );
              })}
              <line x1={0} x2={plotW} y1={plotH} y2={plotH} stroke={CHART_INK.grid} strokeWidth={1} />
            </g>
          </svg>
        )}
        <ChartTooltip state={tooltip} containerWidth={width} />
      </div>
    </div>
  );
}

/** Bar with a 4px rounded data-end and a square baseline. */
function BarRect({
  x,
  y,
  width,
  height,
  color,
  rounded,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  rounded: boolean;
}) {
  const r = rounded ? Math.min(4, width / 2, height) : 0;
  const d =
    r > 0
      ? `M${x},${y + height} V${y + r} Q${x},${y} ${x + r},${y} H${x + width - r} Q${x + width},${y} ${x + width},${y + r} V${y + height} Z`
      : `M${x},${y} h${width} v${height} h${-width} Z`;
  return <path data-testid="bar" d={d} fill={color} />;
}
